// SectionHeading.js
// -----------------------------------------------------------------------------
// Eyebrow / kicker + title + optional subtitle block. Used at the top of most
// page sections for consistent hierarchy.
//
// Props:
//   - eyebrow   : string        (default undef)   small uppercase kicker text
//   - title     : node          (required)        the main heading text
//   - subtitle  : node          (default undef)   supporting paragraph
//   - align     : 'left'|'center' (default 'left') text + block alignment
//   - level     : 'h1'|'h2'|'h3' (default 'h2')   heading typography level
//   - inverted  : boolean       (default false)   light text for dark/brand bg
//   - sx        : object        (default {})       extra Joy sx overrides
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';

const SectionHeading = ({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  level = 'h2',
  inverted = false,
  sx = {},
}) => {
  // Zero-trust: constrain enum-like props.
  const resolvedAlign = align === 'center' ? 'center' : 'left';
  const resolvedLevel = ['h1', 'h2', 'h3'].includes(level) ? level : 'h2';

  // Guard required prop: render nothing if there's no title to show.
  if (title === undefined || title === null || title === '') {
    return null;
  }

  const titleColor = inverted ? 'common.white' : 'text.primary';
  const subtitleColor = inverted ? 'common.white' : 'text.secondary';
  // Inverted subtitles soften the white token via opacity rather than raw rgba().
  const subtitleOpacity = inverted ? 0.85 : 1;
  const eyebrowColor = inverted ? 'accent.300' : 'primary.600';

  return (
    <Box
      sx={{
        textAlign: resolvedAlign,
        mx: resolvedAlign === 'center' ? 'auto' : 0,
        maxWidth: resolvedAlign === 'center' ? '720px' : 'none',
        mb: { xs: 3, md: 4 },
        ...sx,
      }}
    >
      {eyebrow ? (
        <Typography level="title" sx={{ color: eyebrowColor, mb: 1.25, display: 'block' }}>
          {eyebrow}
        </Typography>
      ) : null}

      <Typography level={resolvedLevel} sx={{ color: titleColor }}>
        {title}
      </Typography>

      {subtitle ? (
        <Typography
          level="body-lg"
          sx={{
            color: subtitleColor,
            opacity: subtitleOpacity,
            mt: 1.75,
            mx: resolvedAlign === 'center' ? 'auto' : 0,
            maxWidth: '680px',
          }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
};

SectionHeading.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  align: PropTypes.oneOf(['left', 'center']),
  level: PropTypes.oneOf(['h1', 'h2', 'h3']),
  inverted: PropTypes.bool,
  sx: PropTypes.object,
};

export default SectionHeading;
